import { FormEvent, useEffect, useState } from 'react';
import { RadioButton } from '@/components/core/forms';
import { useMediaQuery } from 'react-responsive';
import { DrawerDialog } from '@/components/core/modals';
import { Button, TimeInput } from '@instamenu/ui-system-next';
import { formatTimeRange } from '@instamenu/formatters';
import { useLocale } from '@instamenu/context-i18n';
import { option, title, subtitle } from './CustomTimeRange.css';

export const allDay = { startTime: undefined, endTime: undefined };

const isValidRange = (r: TimeRange) => Number.isInteger(r.startTime) && Number.isInteger(r.endTime);

export type TimeRange = {
  startTime?: number;
  endTime?: number;
};

export type CustomTimeRangeProps = {
  times: TimeRange;
  setTimes: (times: TimeRange) => void;
};

export default function CustomTimeRange({ times, setTimes }: CustomTimeRangeProps) {
  const [range, setRange] = useState(times);
  const [visible, setVisible] = useState(false);
  const [radio, setRadio] = useState(isValidRange(range) ? 'custom' : 'day');
  const isDesktop = useMediaQuery({ query: `(min-width: 768px)` });
  const { i18n } = useLocale();

  useEffect(() => {
    if (radio === 'day') setTimes(allDay);
    else if (isValidRange(range)) setTimes(range);
  }, [radio, range, setTimes]);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    if (isValidRange(range)) {
      setTimes(range);
    } else {
      setTimes(allDay);
    }
    setVisible(false);
  };

  const TimePickerComponent = (
    <form onSubmit={handleSubmit}>
      <div className={option} onClick={() => setRadio('day')}>
        <div>
          <RadioButton selected={radio === 'day'} />
        </div>
        <div>
          <div className={title}>{i18n.t('salesSummary.customTimeRange.allDay')}</div>
        </div>
      </div>
      <div className={option} onClick={() => setRadio('custom')}>
        <div>
          <RadioButton selected={radio === 'custom'} />
        </div>
        <div>
          <div className={title}>{i18n.t('salesSummary.customTimeRange.custom')}</div>
          <div>
            <div className={subtitle}>{i18n.t('salesSummary.customTimeRange.start')}</div>
            <div>
              <TimeInput
                time={times.startTime}
                onChange={(value) => setRange({ ...range, startTime: value })}
              />
            </div>
          </div>
          <div>
            <div className={subtitle}>{i18n.t('salesSummary.customTimeRange.end')}</div>
            <div>
              <TimeInput
                time={times.endTime}
                onChange={(value) => setRange({ ...range, endTime: value })}
              />
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: isDesktop ? 'none' : 'block' }}>
        <Button type="submit" style={{ width: '100%', margin: '48px 0', boxSizing: 'border-box' }}>
          {i18n.t('salesSummary.customTimeRange.done')}
        </Button>
      </div>
    </form>
  );

  return (
    <DrawerDialog
      visible={visible}
      setVisible={setVisible}
      label={
        radio === 'day' || !isValidRange(range)
          ? i18n.t('salesSummary.customTimeRange.allDay')
          : formatTimeRange(range)
      }
    >
      {TimePickerComponent}
    </DrawerDialog>
  );
}
